// refreshAuthToken attempts to refresh the user's authentication token.
// If the refresh fails due to an expired session (HTTP 401), it logs out the user, creates a new anonymous session, and notifies the user to log in again.
// Returns a Promise that resolves to true if the token was successfully refreshed, or undefined if failed.

import { notifications } from '@mantine/notifications';
import { initCommerceApiWithGlobals } from 'common-api';
import { globals, isBrowser, logger } from 'common-ui';
import { getRecoil, setRecoil } from 'recoil-nexus';
import { authAtom } from '../../atoms/authAtom';
import { createNewCart } from '../../services/cart';
import { getAnonymousToken } from './getAnonymousToken';
import { userAtom } from '../../atoms/userAtom';
import type { UserDetails } from 'tsconfig/types';

const commerceApiExtraConfig = (publicCsrfValue = '') => ({
  webApiExtraHeaders: {
    [globals.authAndWebApiCsrfHeaderName as string]: publicCsrfValue,
  },
});

export const refreshAuthToken = async (): Promise<true | undefined> => {
  if (!isBrowser()) {
    logger.log('refreshAuthToken skipping as not in browser');
    return;
  }

  try {
    const authAtomState = getRecoil(authAtom);
    const commerceApi = initCommerceApiWithGlobals(commerceApiExtraConfig(authAtomState.publicCsrf));
    // call refresh endpoint
    await commerceApi.Auth.refresh();
    return true;
  } catch (error: any) {
    logger.log('API ERROR', error);
    notifications.show({
      message: 'Your session has expired. Please log in again.',
      autoClose: 3000,
      top: 15,
      color: 'red',
    });

    // If the error is a 401, logout the current user and create a new anonymous session
    if (error?.response?.status === 401 && location.pathname !== '/jsp/curity/signin.jsp') {
      logger.error('User session has timed out. Logging out user and creating new anonymous session.');

      try {
        const atgApi = initCommerceApiWithGlobals();
        // Logout current user
        await atgApi.User.hardLogoutViaAPI();

        // get the user details to sync state
        atgApi.User.get()
          .then((user: UserDetails) => {
            setRecoil(userAtom, user);
          })
          .catch((err: unknown) => {
            logger.warn('User.get failed', err);
          });

        // create new anonymous session and cart
        await getAnonymousToken();
        await createNewCart();
      } catch (err) {
        // Catches hardLogout / token / cart failures
        logger.warn('Anonymous session setup failed', err);
      } finally {
        logger.log('Anonymous session flow completed');
      }
    } else {
      throw error;
    }
  }
};
